import type { SchoolStatus } from '@/types/school';

export type WizardMode = 'create' | 'upgradeV2';

// Établissement secondaire saisi avant la création de l'école (persisté après)
export interface EstablishmentDraft {
  localId: number;
  name: string;
  rne: string;
  codeAcademie: string;
  address: string;
  addressComplement: string;
  latitude: number | null;
  longitude: number | null;
  contractType: 'with_contract' | 'without_contract' | '';
}

export interface DeploymentFormData {
  enabled: boolean;
  status: SchoolStatus;
  schoolSourceId: number | null;
  moduleIds: number[];
  // V2 specific
  apiKey: string;
  webhookUrl: string;
  offerId: number | null;
  optionIds: number[];
  tenantGroupId: string | null;
}

export interface WizardFormData {
  // Step 1: Info (previously step 2)
  code: string;
  name: string;
  email: string;
  phone: string;
  emailSender: string;
  domainSender: string;
  address: string;
  addressComplement: string;
  addressHeadoffice: string;
  addressHeadofficeComplement: string;
  networkId: number | null;
  /** Réseau physique rejoint dès la création (définitif) : porte la marque blanche */
  physicalNetworkId: number | null;
  /** Host propre de l'école dans ce réseau (défaut = host du réseau) */
  v2Host: string;
  isEducationNationale: boolean;
  rne: string;
  codeAcademie: string;
  country: string;
  latitude: number | null;
  longitude: number | null;

  // Étape Établissements : secondaires en brouillon (le principal dérive des infos école)
  establishments: EstablishmentDraft[];

  // Step 2: Config (general school config)
  type: string;
  support: string;
  vip: boolean;
  retailerId: number | null;
  technicalContactId: number | null;
  salesContactId: number | null;

  // Step 3: Deployment (new)
  deployV1: DeploymentFormData;
  deployV2: DeploymentFormData;

  // Step 4: Branding
  whiteLabelEntityId: number | null;
  logoFile: File | null;
  logoSigninFile: File | null;
  faviconFile: File | null;
  coverFile: File | null;

  // Legacy fields for backward compatibility
  version: 'v1' | 'v2';
  status: SchoolStatus;
  moduleIds: number[];
  schoolSourceId: number | null;
  apiKeyV2: string;
  webhookUrlV2: string;
}

const initialDeploymentData: DeploymentFormData = {
  enabled: false,
  status: 'demo_empty',
  schoolSourceId: null,
  moduleIds: [],
  apiKey: '',
  webhookUrl: '',
  offerId: null,
  optionIds: [],
  tenantGroupId: null,
};

export const initialFormData: WizardFormData = {
  // Step 1: Info
  code: '',
  name: '',
  email: '',
  phone: '',
  emailSender: 'no-reply@ecole-futee.com',
  domainSender: '',
  address: '',
  addressComplement: '',
  addressHeadoffice: '',
  addressHeadofficeComplement: '',
  networkId: null,
  physicalNetworkId: null,
  v2Host: '',
  isEducationNationale: false,
  rne: '',
  codeAcademie: '',
  country: 'FR',
  latitude: null,
  longitude: null,

  establishments: [],

  // Step 2: Config
  type: 'private_out_of_contract',
  support: '0',
  vip: false,
  retailerId: null,
  technicalContactId: null,
  salesContactId: null,

  // Step 3: Deployment
  deployV1: { ...initialDeploymentData },
  deployV2: { ...initialDeploymentData },

  // Step 4: Branding
  whiteLabelEntityId: null,
  logoFile: null,
  logoSigninFile: null,
  faviconFile: null,
  coverFile: null,

  // Legacy (for backward compatibility during migration)
  version: 'v1',
  status: 'demo_empty',
  moduleIds: [],
  schoolSourceId: null,
  apiKeyV2: '',
  webhookUrlV2: '',
};

export const WIZARD_STEPS = [
  { id: 'info', label: 'Informations' },
  { id: 'establishments', label: 'Établissements' },
  { id: 'config', label: 'Configuration' },
  { id: 'branding', label: 'Branding' },
  { id: 'summary', label: 'Résumé' },
  { id: 'deployment', label: 'Déploiement' },
];

// Indices des étapes (mêmes étapes en création et en passage V2)
export const STEP_INFO = 0;
export const STEP_ESTABLISHMENTS = 1;
export const STEP_CONFIG = 2;
export const STEP_BRANDING = 3;
export const STEP_SUMMARY = 4;
export const STEP_DEPLOYMENT = 5;
